import { partnersData } from "../data/mock";
import { Handshake } from "lucide-react";
import { motion } from "framer-motion";

const MarqueeRow = ({ images, direction = "left", speed = 40 }) => {
  const duplicated = [...images, ...images];

  return (
    <div className="relative overflow-hidden w-full" style={{ height: "120px" }}>
      <div
        className="flex items-center gap-0 absolute"
        style={{
          animation: `marquee-${direction} ${speed}s linear infinite`,
          whiteSpace: "nowrap",
        }}
      >
        {duplicated.map((img, i) => (
          <div
            key={i}
            className="flex-shrink-0 flex items-center justify-center"
            style={{
              height: "110px",
              padding: "0 8px",
            }}
          >
            <div
              className="rounded-xl overflow-hidden flex items-center justify-center"
              style={{
                height: "100px",
                width: "auto",
                background: "#FFFFFF",
                padding: "8px 20px",
              }}
            >
              <img
                src={img}
                alt="Partner logos"
                style={{
                  height: "80px",
                  width: "auto",
                  maxWidth: "500px",
                  objectFit: "contain",
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes marquee-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes marquee-right {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

const PartnerCard = ({ partner, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, delay: index * 0.04 }}
      className="group flex items-center gap-4 rounded-xl cursor-default"
      style={{
        padding: "18px 22px",
        background: "#121530",
        border: "1px solid rgba(123,104,238,0.08)",
        transition: "all 0.35s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(123,104,238,0.25)";
        e.currentTarget.style.background = "#171B36";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(123,104,238,0.08)";
        e.currentTarget.style.background = "#121530";
      }}
    >
      {/* Logo */}
      <div
        className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-lg overflow-hidden"
        style={{ background: "#FFFFFF" }}
      >
        <img
          src={partner.logo}
          alt={partner.name}
          className="w-7 h-7 object-contain"
          onError={(e) => {
            e.target.style.display = "none";
            e.target.parentElement.innerHTML = `<span style="font-family:'Kode Mono',monospace;font-size:14px;font-weight:700;color:#7B68EE">${partner.name.charAt(0)}</span>`;
          }}
        />
      </div>

      {/* Info */}
      <div className="min-w-0">
        <div
          style={{
            fontFamily: "'Kode Mono', monospace",
            fontSize: "14px",
            fontWeight: 600,
            color: "#F0F0FF",
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {partner.name}
        </div>
        <div
          style={{
            fontFamily: "'Kode Mono', monospace",
            fontSize: "11px",
            color: "#6B6B8D",
            marginTop: "2px",
          }}
        >
          {partner.category}
        </div>
      </div>
    </motion.div>
  );
};

export const PartnersSection = () => {
  const { partners, partnerImages, headline, description } = partnersData;

  const row1 = partnerImages.slice(0, 3);
  const row2 = partnerImages.slice(2, 5);

  return (
    <section
      id="partners"
      className="relative w-full overflow-hidden"
      style={{ background: "#0B0E1E", padding: "120px 0" }}
    >
      {/* Section Header */}
      <div style={{ padding: "0 7.6923%" }}>
        <div className="max-w-[1400px] mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-[2px] rounded-full" style={{ background: "#7B68EE" }} />
              <span
                className="text-sm tracking-widest uppercase"
                style={{ color: "#7B68EE", fontFamily: "'Kode Mono', monospace" }}
              >
                Our Partners
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end">
              <div>
                <h2
                  style={{
                    fontFamily: "'Kode Mono', monospace",
                    fontWeight: 600,
                    fontSize: "clamp(32px, 4vw, 48px)",
                    color: "#F0F0FF",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                    marginBottom: "16px",
                  }}
                >
                  {headline}
                </h2>
              </div>
              <div>
                <p
                  style={{
                    fontFamily: "'Kode Mono', monospace",
                    fontSize: "15px",
                    color: "#8585A8",
                    lineHeight: 1.7,
                    maxWidth: "580px",
                  }}
                >
                  {description}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scrolling Logo Marquee */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="mb-6"
      >
        <MarqueeRow images={row1} direction="left" speed={35} />
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="mb-16"
      >
        <MarqueeRow images={row2} direction="right" speed={40} />
      </motion.div>

      {/* Partners Grid */}
      <div style={{ padding: "0 7.6923%" }}>
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-3 mb-8"
          >
            <Handshake size={18} style={{ color: "#6B6B8D" }} />
            <span
              style={{
                fontFamily: "'Kode Mono', monospace",
                fontSize: "13px",
                color: "#6B6B8D",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
              }}
            >
              {partners.length} Technology Partners
            </span>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3">
            {partners.map((partner, index) => (
              <PartnerCard key={partner.name} partner={partner} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
